import React, { useState, useRef, useEffect } from 'react';
import { Avatar, Button, Input, Spin } from 'antd';
import { ArrowLeftOutlined, SendOutlined, UserOutlined, CheckOutlined } from '@ant-design/icons';
import { useChatStore } from '../../stores/chatStore';
import css from './Chat.module.css';
import dayjs from 'dayjs';

const { TextArea } = Input;

function ChatMessages({ conversation, userId, onBack }) {
    const [text, setText] = useState('');
    const messagesEndRef = useRef(null);
    const typingTimeoutRef = useRef(null);

    const {
        messages,
        messagesLoading,
        sendMessage,
        startTyping,
        stopTyping,
        typingUsers
    } = useChatStore();

    const isTyping = typingUsers[conversation.id];

    // Auto scroll
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = () => {
        if (!text.trim()) return;
        sendMessage(text);
        setText('');
        stopTyping();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleChange = (e) => {
        setText(e.target.value);
        startTyping();
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => {
            stopTyping();
        }, 2000);
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        return dayjs(dateStr).format('HH:mm');
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = dayjs(dateStr);
        const now = dayjs();
        if (d.isSame(now, 'day')) return 'Bugun';
        if (d.isSame(now.subtract(1, 'day'), 'day')) return 'Kecha';
        return d.format('DD MMMM YYYY');
    };

    // Xabarlarni sana bo'yicha guruhlash
    const groupedMessages = () => {
        const groups = [];
        let lastDate = '';
        messages.forEach(msg => {
            const date = dayjs(msg.created_at).format('YYYY-MM-DD');
            if (date !== lastDate) {
                groups.push({ type: 'date', date: msg.created_at });
                lastDate = date;
            }
            groups.push({ type: 'message', ...msg });
        });
        return groups;
    };

    if (messagesLoading) {
        return (
            <div className={css.messagesContainer}>
                <div className={css.messagesHeader}>
                    <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack} />
                    <div className={css.messagesHeaderInfo}>
                        <p className={css.messagesHeaderName}>
                           {conversation.other_user_name} <br /> <span style={{ fontSize: 12, color: '#666' }}>{conversation.provider_name}</span>
                        </p>
                    </div>
                </div>
                <div className={css.loadingCenter}>
                    <Spin />
                </div>
            </div>
        );
    }

    return (
        <div className={css.messagesContainer}>
            {/* Header */}
            <div className={css.messagesHeader}>
                <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack} />
                <Avatar
                    size={36}
                    icon={<UserOutlined />}
                    src={conversation.other_user_avatar}
                    style={{ backgroundColor: '#1890ff' }}
                />
                <div className={css.messagesHeaderInfo}>
                    <p className={css.messagesHeaderName}>
                       {conversation.other_user_name} <br /> <span style={{ fontSize: 12, color: '#666' }}>{conversation.provider_name}</span>
                    </p>
                    <p className={css.messagesHeaderStatus}>
                        {isTyping ? (
                            <span className={css.typingStatus}>yozmoqda...</span>
                        ) : conversation.is_online ? 'Online' : ''}
                    </p>
                </div>
            </div>

            {/* Messages */}
            <div className={css.messagesArea}>
                {groupedMessages().map((item, idx) => {
                    if (item.type === 'date') {
                        return (
                            <div key={`date-${idx}`} className={css.dateSeparator}>
                                <span>{formatDate(item.date)}</span>
                            </div>
                        );
                    }

                    const isMine = item.sender_id === userId;
                    return (
                        <div
                            key={item.id}
                            className={`${css.messageRow} ${isMine ? css.messageRowMine : css.messageRowOther}`}
                        >
                            <div className={`${css.messageBubble} ${isMine ? css.messageMine : css.messageOther}`}>
                                <div>{item.text}</div>
                                <div className={`${css.messageTime} ${isMine ? css.messageTimeMine : css.messageTimeOther}`}>
                                    <span>{formatTime(item.created_at)}</span>
                                    {isMine && item.is_read === 1 && (
                                        <CheckOutlined className={css.messageRead} />
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className={css.inputArea}>
                <TextArea
                    value={text}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Xabar yozing..."
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    style={{ flex: 1 }}
                />
                <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleSend}
                    disabled={!text.trim()}
                    className={css.sendBtn}
                />
            </div>
        </div>
    );
}

export default ChatMessages;
